import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import type {
  RotationData,
  PlayerRotation,
  ScoreTimelinePoint,
  StintStats,
} from './types';

// PBPStats API base URL (direct access)
const PBPSTATS_BASE_URL = 'https://api.pbpstats.com';

// Derive the current NBA season string (e.g. "2025-26") from today's date.
// The NBA season starts in October, so before October we're still in the
// previous season (e.g. January 2026 → "2025-26").
function getCurrentNbaSeason(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-indexed: 0=Jan, 9=Oct
  const startYear = month >= 9 ? year : year - 1;
  const endYear = startYear + 1;
  return `${startYear}-${String(endYear).slice(2)}`;
}

// Static NBA teams list
export const NBA_TEAMS = [
  { id: '1610612737', abbrev: 'ATL', name: 'Atlanta Hawks' },
  { id: '1610612738', abbrev: 'BOS', name: 'Boston Celtics' },
  { id: '1610612751', abbrev: 'BKN', name: 'Brooklyn Nets' },
  { id: '1610612766', abbrev: 'CHA', name: 'Charlotte Hornets' },
  { id: '1610612741', abbrev: 'CHI', name: 'Chicago Bulls' },
  { id: '1610612739', abbrev: 'CLE', name: 'Cleveland Cavaliers' },
  { id: '1610612742', abbrev: 'DAL', name: 'Dallas Mavericks' },
  { id: '1610612743', abbrev: 'DEN', name: 'Denver Nuggets' },
  { id: '1610612765', abbrev: 'DET', name: 'Detroit Pistons' },
  { id: '1610612744', abbrev: 'GSW', name: 'Golden State Warriors' },
  { id: '1610612745', abbrev: 'HOU', name: 'Houston Rockets' },
  { id: '1610612754', abbrev: 'IND', name: 'Indiana Pacers' },
  { id: '1610612746', abbrev: 'LAC', name: 'LA Clippers' },
  { id: '1610612747', abbrev: 'LAL', name: 'Los Angeles Lakers' },
  { id: '1610612763', abbrev: 'MEM', name: 'Memphis Grizzlies' },
  { id: '1610612748', abbrev: 'MIA', name: 'Miami Heat' },
  { id: '1610612749', abbrev: 'MIL', name: 'Milwaukee Bucks' },
  { id: '1610612750', abbrev: 'MIN', name: 'Minnesota Timberwolves' },
  { id: '1610612740', abbrev: 'NOP', name: 'New Orleans Pelicans' },
  { id: '1610612752', abbrev: 'NYK', name: 'New York Knicks' },
  { id: '1610612760', abbrev: 'OKC', name: 'Oklahoma City Thunder' },
  { id: '1610612753', abbrev: 'ORL', name: 'Orlando Magic' },
  { id: '1610612755', abbrev: 'PHI', name: 'Philadelphia 76ers' },
  { id: '1610612756', abbrev: 'PHX', name: 'Phoenix Suns' },
  { id: '1610612757', abbrev: 'POR', name: 'Portland Trail Blazers' },
  { id: '1610612758', abbrev: 'SAC', name: 'Sacramento Kings' },
  { id: '1610612759', abbrev: 'SAS', name: 'San Antonio Spurs' },
  { id: '1610612761', abbrev: 'TOR', name: 'Toronto Raptors' },
  { id: '1610612762', abbrev: 'UTA', name: 'Utah Jazz' },
  { id: '1610612764', abbrev: 'WAS', name: 'Washington Wizards' },
] as const;

export interface NbaTeam {
  id: string;
  abbrev: string;
  name: string;
}

export interface RecentGame {
  gameId: string;
  gameDate: string;
  homeTeamId: string;
  homeTeamAbbrev: string;
  homeScore: number;
  awayTeamId: string;
  awayTeamAbbrev: string;
  awayScore: number;
  isFinal: boolean;
}

// Hook to get NBA teams (static data, no API call)
export function useTeams() {
  return useQuery<NbaTeam[]>({
    queryKey: ['rotations-teams'],
    queryFn: async () => {
      // Return static teams - no API call needed
      return [...NBA_TEAMS];
    },
    staleTime: Infinity, // Teams never change
  });
}

// PBPStats API response types
interface PBPStatsGameRaw {
  GameId: string;
  Date: string;
  HomeTeamId: number;
  AwayTeamId: number;
  HomePoints: number;
  AwayPoints: number;
}

// Hook to get recent games for a team
export function useGames(teamId: string | null) {
  const season = getCurrentNbaSeason();

  return useQuery<RecentGame[]>({
    queryKey: ['rotations-games', teamId, season],
    queryFn: async () => {
      if (!teamId) return [];

      // Call PBPStats API directly
      const response = await fetch(
        `${PBPSTATS_BASE_URL}/get-games/nba?Season=${season}&SeasonType=Regular%20Season`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch games');
      }

      const data = await response.json();
      const allGames = data.results || [];

      // Filter games for this team and transform
      const teamGames: RecentGame[] = [];
      for (const game of allGames) {
        const homeId = String(game.HomeTeamId);
        const awayId = String(game.AwayTeamId);

        if (homeId === teamId || awayId === teamId) {
          // Look up team abbreviations from our static list
          const homeTeam = NBA_TEAMS.find(t => t.id === homeId);
          const awayTeam = NBA_TEAMS.find(t => t.id === awayId);

          teamGames.push({
            gameId: String(game.GameId),
            gameDate: game.Date,
            homeTeamId: homeId,
            homeTeamAbbrev: homeTeam?.abbrev || 'UNK',
            homeScore: game.HomePoints || 0,
            awayTeamId: awayId,
            awayTeamAbbrev: awayTeam?.abbrev || 'UNK',
            awayScore: game.AwayPoints || 0,
            isFinal: true, // Historical games are final
          });
        }
      }

      // Sort by date descending (most recent first) and limit to 14
      teamGames.sort((a, b) => new Date(b.gameDate).getTime() - new Date(a.gameDate).getTime());
      return teamGames.slice(0, 14);
    },
    enabled: teamId !== null,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Hook to get rotation data for a game
export function useRotation(gameId: string | null) {
  return useQuery<RotationData>({
    queryKey: ['rotations-rotation', gameId],
    queryFn: async () => {
      if (!gameId) throw new Error('Game ID required');

      // Fetch game flow data (contains stints and score margins)
      const gameFlowResponse = await fetch(
        `${PBPSTATS_BASE_URL}/live/game/${gameId}/game-flow`
      );

      if (!gameFlowResponse.ok) {
        throw new Error('Failed to fetch game flow data');
      }

      const gameFlowData = await gameFlowResponse.json();

      // Fetch game stats for box score info
      const gameStatsResponse = await fetch(
        `${PBPSTATS_BASE_URL}/get-game-stats?GameId=${gameId}&Type=Player`
      );

      if (!gameStatsResponse.ok) {
        throw new Error('Failed to fetch game stats');
      }

      const gameStatsData = await gameStatsResponse.json();

      // Fetch possession-by-possession data for stint-level stats
      let possessionsData: any = null;
      try {
        const possessionsResponse = await fetch(
          `${PBPSTATS_BASE_URL}/live/game/${gameId}/possession-by-possession`
        );
        if (possessionsResponse.ok) {
          possessionsData = await possessionsResponse.json();
        }
      } catch {
        // Non-critical — hover cards will just lack stint stats
      }

      return transformPBPStatsResponse(gameFlowData, gameStatsData, gameId, possessionsData);
    },
    enabled: gameId !== null,
    staleTime: 1000 * 60 * 60, // 1 hour (game data doesn't change after game ends)
  });
}

// --- Possession-by-possession event parsing helpers ---

interface PlayerEvent {
  time: number; // game elapsed seconds
  type: string; // 'fgm2' | 'fga2' | 'fgm3' | 'fga3' | 'ftm' | 'fta' | 'reb' | 'ast' | 'stl' | 'blk' | 'tov' | 'foul'
}

/**
 * Build lookup maps from abbreviated PBP names (e.g. "C. Cunningham") to
 * full box-score names (e.g. "Cade Cunningham").
 */
function buildNameLookup(fullNames: string[]): (abbrev: string) => string {
  const abbrevToFull: Record<string, string> = {};
  const lastnameToFull: Record<string, string | null> = {};

  for (const full of fullNames) {
    const parts = full.split(' ');
    if (parts.length < 2) continue;
    const firstInitial = parts[0][0];
    const lastName = parts[parts.length - 1];

    // "C. Cunningham" and "C Cunningham"
    abbrevToFull[`${firstInitial}. ${lastName}`.toLowerCase()] = full;
    abbrevToFull[`${firstInitial} ${lastName}`.toLowerCase()] = full;

    // Last-name-only (only if unique)
    const lnLower = lastName.toLowerCase();
    if (!(lnLower in lastnameToFull)) {
      lastnameToFull[lnLower] = full;
    } else {
      lastnameToFull[lnLower] = null; // collision — disable
    }
  }

  return (abbrev: string): string => {
    const lower = abbrev.toLowerCase();
    if (abbrevToFull[lower]) return abbrevToFull[lower];
    const parts = abbrev.split(' ');
    const ln = parts[parts.length - 1].toLowerCase();
    if (lastnameToFull[ln]) return lastnameToFull[ln]!;
    return abbrev;
  };
}

/** Convert game-clock remaining (MM:SS in a period) to elapsed game seconds. */
function clockToGameTime(minutes: number, seconds: number, period: number, periodLength = 720): number {
  const clockSeconds = minutes * 60 + seconds;
  const timeInPeriod = periodLength - clockSeconds;
  return (period - 1) * periodLength + timeInPeriod;
}

/**
 * Parse all possession event strings and build per-player event arrays.
 * Each event string looks like "8:45.3 C. Cunningham 3pt Jump Shot (12 PTS) (J. Ivey 3 AST)"
 *
 * Period tracking uses the `start_period` boolean flag on each possession
 * object (matching the pbpstats API format), NOT text parsing from events.
 */
function buildAllPlayerEvents(
  possessionsData: any,
  resolveName: (abbrev: string) => string,
): Record<string, PlayerEvent[]> {
  const events: Record<string, PlayerEvent[]> = {};

  const addEvent = (name: string, time: number, type: string) => {
    const resolved = resolveName(name.trim());
    if (!events[resolved]) events[resolved] = [];
    events[resolved].push({ time, type });
  };

  // API returns possessions under game_data.possessions or root possessions
  let possList: any[] =
    possessionsData?.game_data?.possessions ??
    possessionsData?.possessions ??
    [];

  // Period starts at 0, incremented by start_period flag → first period = 1
  let currentPeriod = 0;
  const periodLength = 720;

  for (const poss of possList) {
    // Period boundary: possession-level flag
    if (poss.start_period) {
      currentPeriod++;
    }

    const possEvents: string[] = poss.events || [];

    for (const event of possEvents) {
      if (typeof event !== 'string') continue;

      // Extract clock time (MM:SS.D at the start of the string)
      const timeMatch = event.match(/^(\d+):(\d+)\.\d+/);
      if (!timeMatch) continue;
      const clockMin = parseInt(timeMatch[1], 10);
      const clockSec = parseInt(timeMatch[2], 10);
      const gameTime = clockToGameTime(clockMin, clockSec, currentPeriod, periodLength);

      const eventLower = event.toLowerCase();
      const afterTime = event.slice(timeMatch[0].length).trim();
      const afterTimeLower = afterTime.toLowerCase();

      // Shot types regex fragment
      const shotTypes = /(?:\d+['"]?\s*)?(?:3pt|step back|pullup|pull-up|driving|running|fadeaway|fade away|jump|hook|layup|lay-up|dunk|finger roll|tip|floating|float|bank|cutting|alley oop|putback|put-back|turnaround|turn around|reverse|slam|shot)/i;

      // --- Made shot (contains "PTS)", no "miss", no "free throw") ---
      if (eventLower.includes(' pts)') && !eventLower.includes('miss ') && !eventLower.includes('free throw')) {
        const nameMatch = afterTime.match(new RegExp(`^(.+?)\\s+${shotTypes.source}`, 'i'));
        if (nameMatch) {
          const is3 = eventLower.includes('3pt');
          addEvent(nameMatch[1], gameTime, is3 ? 'fgm3' : 'fgm2');
          addEvent(nameMatch[1], gameTime, is3 ? 'fga3' : 'fga2');

          // Assist — "(J. Ivey 3 AST)"
          const astMatch = event.match(/\(([A-Z][.\s]*[A-Za-z'\-]+(?:\s+[A-Za-z'\-]+)*)\s+\d+\s*AST\)/i);
          if (astMatch) {
            addEvent(astMatch[1], gameTime, 'ast');
          }
        }
      }

      // --- Missed shot ---
      else if (afterTimeLower.startsWith('miss ') || eventLower.substring(0, 20).includes(' miss ')) {
        const missMatch = afterTime.match(new RegExp(`^miss\\s+(.+?)\\s+${shotTypes.source}`, 'i'));
        if (missMatch) {
          const is3 = eventLower.includes('3pt');
          addEvent(missMatch[1], gameTime, is3 ? 'fga3' : 'fga2');
        }
        // Block on the miss
        const blkMatch = event.match(/\(([A-Z][.\s]*[A-Za-z'\-]+(?:\s+[A-Za-z'\-]+)*)\s+\d+\s*BLK\)/i);
        if (blkMatch) {
          addEvent(blkMatch[1], gameTime, 'blk');
        }
      }

      // --- Free throw ---
      else if (eventLower.includes('free throw')) {
        const ftMatch = afterTime.match(/^(?:miss\s+)?(.+?)\s+free throw/i);
        if (ftMatch) {
          const isMiss = afterTimeLower.startsWith('miss');
          addEvent(ftMatch[1], gameTime, 'fta');
          if (!isMiss) {
            addEvent(ftMatch[1], gameTime, 'ftm');
          }
        }
      }

      // --- Rebound ---
      else if (eventLower.includes('rebound') && !eventLower.includes('team')) {
        const rebMatch = afterTime.match(/^(.+?)\s+rebound/i);
        if (rebMatch && !/team/i.test(rebMatch[1])) {
          addEvent(rebMatch[1], gameTime, 'reb');
        }
      }

      // --- Steal ---
      else if (eventLower.includes('steal')) {
        const stlMatch = afterTime.match(/^(.+?)\s+steal/i);
        if (stlMatch) {
          addEvent(stlMatch[1], gameTime, 'stl');
        }
      }

      // --- Block (standalone, not on a miss line) ---
      else if (eventLower.includes('block') && !eventLower.includes('blocked')) {
        const blkMatch = afterTime.match(/^(.+?)\s+block/i);
        if (blkMatch) {
          addEvent(blkMatch[1], gameTime, 'blk');
        }
      }

      // --- Turnover ---
      else if (eventLower.includes('turnover') && !eventLower.includes('team')) {
        const tovMatch = afterTime.match(/^(.+?)\s+(?:bad pass|lost ball|traveling|step out of bounds|offensive foul|out of bounds|double dribble|palming|kicked ball|3 second|5 second|8 second|shot clock|back court|lane|illegal assist|discontinued dribble|inbound|punched ball)?\s*(?:out[- ]of[- ]bounds\s+)?turnover/i);
        if (tovMatch && !/team/i.test(tovMatch[1])) {
          addEvent(tovMatch[1], gameTime, 'tov');
        }
      }

      // --- Foul (non-offensive, non-technical) ---
      if (eventLower.includes('foul') && !eventLower.includes('offensive') && !eventLower.includes('technical')) {
        const foulMatch = afterTime.match(/^(.+?)\s+(?:shooting\s+|away[- ]from[- ]play\s+|loose\s+ball\s+)?(?:personal|flagrant|clear path)?\s*foul/i);
        if (foulMatch) {
          addEvent(foulMatch[1], gameTime, 'foul');
        }
      }
    }
  }

  return events;
}

/** Calculate stint stats from player events within a time window. */
function calculateStintStats(
  playerEvents: PlayerEvent[] | undefined,
  start: number,
  end: number,
): StintStats {
  const stats: StintStats = {
    pts: 0, fgm: 0, fga: 0, fg3m: 0, fg3a: 0,
    ftm: 0, fta: 0, reb: 0, ast: 0, stl: 0, blk: 0, tov: 0,
  };

  if (!playerEvents) return stats;

  for (const ev of playerEvents) {
    if (ev.time < start || ev.time > end) continue;
    switch (ev.type) {
      case 'fgm2': stats.fgm++; stats.pts += 2; break;
      case 'fga2': stats.fga++; break;
      case 'fgm3': stats.fgm++; stats.fg3m++; stats.pts += 3; break;
      case 'fga3': stats.fga++; stats.fg3a++; break;
      case 'ftm': stats.ftm++; stats.pts++; break;
      case 'fta': stats.fta++; break;
      case 'reb': stats.reb++; break;
      case 'ast': stats.ast++; break;
      case 'stl': stats.stl++; break;
      case 'blk': stats.blk++; break;
      case 'tov': stats.tov++; break;
    }
  }

  return stats;
}

/** Get array of game-time seconds when a player committed fouls. */
function getFoulTimes(playerEvents: PlayerEvent[] | undefined): number[] {
  if (!playerEvents) return [];
  return playerEvents.filter(ev => ev.type === 'foul').map(ev => ev.time);
}

// Transform PBPStats API responses into our frontend format
function transformPBPStatsResponse(gameFlow: any, gameStats: any, gameId: string, possessionsData?: any): RotationData {
  const gameData = gameFlow.game_data || {};
  const gameInfo = gameData.game_info || {};

  // Get team info
  const homeTeamId = String(gameStats.home_team_id || '');
  const awayTeamId = String(gameStats.away_team_id || '');
  const homeTeam = NBA_TEAMS.find(t => t.id === homeTeamId);
  const awayTeam = NBA_TEAMS.find(t => t.id === awayTeamId);

  const gameDuration = gameData.max_time || 2880;

  // Build stats lookups from box score
  const statsData = gameStats.stats || {};
  const homeBox = statsData.Home?.FullGame || [];
  const awayBox = statsData.Away?.FullGame || [];

  const buildStatsLookup = (boxData: any[]): Record<string, any> => {
    const lookup: Record<string, any> = {};
    for (const player of boxData) {
      const name = player.Name || '';
      if (name) {
        lookup[name] = {
          pts: player.Points || 0,
          reb: player.Rebounds || 0,
          ast: player.Assists || 0,
          stl: player.Steals || 0,
          blk: player.Blocks || 0,
          pf: player.PersonalFouls || player.Fouls || 0,
        };
      }
    }
    return lookup;
  };

  const homeStatsLookup = buildStatsLookup(homeBox);
  const awayStatsLookup = buildStatsLookup(awayBox);

  // Get score margins for +/- calculation
  const scoreMargins = gameData.score_margins || [];

  const getMarginAtTime = (timeSec: number): number => {
    for (const sm of scoreMargins) {
      if (sm.start_time <= timeSec && timeSec <= (sm.end_time || timeSec + 1)) {
        return sm.margin;
      }
    }
    return 0;
  };

  const calculateStintPlusMinus = (start: number, end: number, isHome: boolean): number => {
    const startMargin = getMarginAtTime(start);
    const endMargin = getMarginAtTime(end);
    const diff = endMargin - startMargin;
    return isHome ? diff : -diff;
  };

  // Build possession event data if available
  let allPlayerEvents: Record<string, PlayerEvent[]> = {};
  if (possessionsData) {
    // Collect all full names from box score data
    const allFullNames: string[] = [
      ...homeBox.map((p: any) => p.Name || ''),
      ...awayBox.map((p: any) => p.Name || ''),
    ].filter(Boolean);

    const resolveName = buildNameLookup(allFullNames);
    allPlayerEvents = buildAllPlayerEvents(possessionsData, resolveName);
  }

  // Process stints for a team
  const processStints = (stintsData: any[], statsLookup: Record<string, any>, isHome: boolean): PlayerRotation[] => {
    const players: PlayerRotation[] = [];

    for (const playerData of stintsData) {
      const name = playerData.name || '';
      const rawStints = playerData.stints || [];

      const processedStints: any[] = [];
      let totalPlusMinus = 0;
      let totalSeconds = 0;

      // Get foul times for this player to track cumulative count
      const playerEvents = allPlayerEvents[name];
      const foulTimes = getFoulTimes(playerEvents);
      let cumulativeFouls = 0;

      for (const stint of rawStints) {
        const start = stint.start_time || 0;
        const end = stint.end_time;

        if (end == null) continue;

        const stintPM = calculateStintPlusMinus(start, end, isHome);
        totalPlusMinus += stintPM;
        totalSeconds += (end - start);

        const marginStart = getMarginAtTime(start);
        const marginEnd = getMarginAtTime(end);

        // Calculate stint-level stats from possession data
        const stintStats = possessionsData
          ? calculateStintStats(playerEvents, Math.floor(start), Math.floor(end))
          : undefined;

        // Count fouls in this stint
        const foulsInThisStint = foulTimes.filter(t => t >= Math.floor(start) && t <= Math.floor(end)).length;
        cumulativeFouls += foulsInThisStint;

        processedStints.push({
          startTime: Math.floor(start),
          endTime: Math.floor(end),
          period: Math.floor(start / 720) + 1,
          plusMinus: stintPM,
          marginStart: isHome ? marginStart : -marginStart,
          marginEnd: isHome ? marginEnd : -marginEnd,
          stats: stintStats,
          foulsInStint: foulsInThisStint,
          foulsTotal: cumulativeFouls,
        });
      }

      if (processedStints.length > 0) {
        const playerStats = statsLookup[name] || { pts: 0, reb: 0, ast: 0, stl: 0, blk: 0, pf: 0 };

        players.push({
          playerId: '',
          playerName: name,
          totalMinutes: Math.round((totalSeconds / 60) * 10) / 10,
          totalPlusMinus,
          stats: playerStats,
          stints: processedStints,
        });
      }
    }

    // Sort: starters first (by minutes desc), then bench (by first appearance)
    players.sort((a, b) => {
      const aFirstStart = a.stints[0]?.startTime ?? Infinity;
      const bFirstStart = b.stints[0]?.startTime ?? Infinity;
      const aIsStarter = aFirstStart === 0;
      const bIsStarter = bFirstStart === 0;

      if (aIsStarter && !bIsStarter) return -1;
      if (!aIsStarter && bIsStarter) return 1;
      if (aIsStarter && bIsStarter) return b.totalMinutes - a.totalMinutes;
      return aFirstStart - bFirstStart;
    });

    return players;
  };

  const homeStintsRaw = gameData.home_stints || [];
  const awayStintsRaw = gameData.visitor_stints || [];

  const homeRotations = processStints(homeStintsRaw, homeStatsLookup, true);
  const awayRotations = processStints(awayStintsRaw, awayStatsLookup, false);

  // Build score timeline
  const scoreTimeline: ScoreTimelinePoint[] = [];
  for (const sm of scoreMargins) {
    scoreTimeline.push({
      time: Math.floor(sm.start_time),
      scoreDiff: sm.margin,
      homeScore: 0,
      awayScore: 0,
    });
  }
  if (scoreMargins.length > 0) {
    const last = scoreMargins[scoreMargins.length - 1];
    scoreTimeline.push({
      time: Math.floor(last.end_time || gameDuration),
      scoreDiff: last.margin,
      homeScore: 0,
      awayScore: 0,
    });
  }

  return {
    gameInfo: {
      gameId,
      date: gameStats.date || '',
      isFinal: gameInfo.status === 'Final',
      home: {
        id: homeTeamId,
        abbrev: homeTeam?.abbrev || gameStats.home_team_abbreviation || 'HOME',
        score: gameInfo.home_score || 0,
      },
      away: {
        id: awayTeamId,
        abbrev: awayTeam?.abbrev || gameStats.away_team_abbreviation || 'AWAY',
        score: gameInfo.visitor_score || 0,
      },
    },
    homeRotations,
    awayRotations,
    scoreTimeline,
    gameDuration,
  };
}

// --- Subscription Check Hook ---

interface AuthCheckResponse {
  result: boolean;
  userId?: string;
  authTier?: string;
}

export function useSubscriptionCheck() {
  const { isAuthenticated, isLoading: isAuthLoading, user } = useAuth();
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState<boolean | null>(null);

  useEffect(() => {
    const checkSubscription = async () => {
      // Skip if not authenticated or still loading auth
      if (isAuthLoading || !isAuthenticated || !user?.email) {
        setHasActiveSubscription(null);
        return;
      }

      setIsCheckingSubscription(true);
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const email = user.email!;

        // Check for NBA subscription for ROTATIONS app
        const response = await fetch(
          `${apiUrl}/api/auth/stripe?email=${encodeURIComponent(email)}&sport=NBA&appName=ROTATIONS`
        );

        if (response.ok) {
          const data: AuthCheckResponse = await response.json();
          setHasActiveSubscription(data.result === true);
        } else {
          setHasActiveSubscription(false);
        }
      } catch (error) {
        console.error('Failed to check subscription:', error);
        setHasActiveSubscription(false);
      } finally {
        setIsCheckingSubscription(false);
      }
    };

    checkSubscription();
  }, [isAuthenticated, isAuthLoading, user?.email]);

  return {
    isLoading: isAuthLoading || isCheckingSubscription,
    isAuthenticated,
    hasActiveSubscription,
  };
}
